import { SignalRConnection } from '@henkkelder/ng2-signalr';
import { createFeature, createReducer, on } from '@ngrx/store';
import { TagCategory } from '@nibras/core/common';
import { AppConstants } from '../model/constants.model';
import { PortVisual } from '../model/dcAttributeTemplate.model';
import { dcKPIData } from '../model/dcKPIs.model';
import { dcTag } from '../model/dcTag.model';
import { RoleAssignment, User } from '../model/user.model';
import { SharedCommonService } from '../shared-common.service';
import {
  dropdownDataAction,
  portVisualizationAction,
  resizeAppAction,
  selectedKPIAction,
  setSignalRConnectionAction,
  userDetailsResultAction,
  userLogoutAction,
  userProfileAppsResultAction,
  userProfileSwitchProfileTagAction,
  userProfileSwitchRoleAction,
  userProfileSwitchRoleResultAction
} from './core-store.actions';

export interface CoreState {
  defaultTags: dcTag[];
  appTags: dcTag[];
  userMenuTags: dcTag[];
  currentUserRole: RoleAssignment;
  userDetails: User;
  isSwitchingRole: boolean;
  signalRConnection: SignalRConnection;
  dropdownData: any; //TODO: to be moved to app-cache-service
  resizeApp: boolean; //TODO: to be moved to app-cache-service
  currentProfileTag: dcTag;
  portTreeData: PortVisual; //TODO: to be moved to app-cache-service
  selectedKPI: dcKPIData; //TODO: to be moved to app-cache-service
}

const initialState: CoreState = {
  defaultTags: new Array<dcTag>(),
  appTags: new Array<dcTag>(),
  userMenuTags: new Array<dcTag>(),
  currentUserRole: null,
  userDetails: null,
  signalRConnection: null,
  dropdownData: {},
  resizeApp: false,
  currentProfileTag: undefined,
  portTreeData: null,
  selectedKPI: null,
  isSwitchingRole: null
};

const SELECTED_PROFILE_TAG_ID_KEY = 'selectedProfileTagId';

const coreStoreReducer = createReducer(
  initialState,

  on(resizeAppAction, state => ({
    ...state,
    resizeApp: !state.resizeApp
  })),

  on(userDetailsResultAction, (state, { userDetails }) => ({
    ...state,
    ...{
      //userMenuTags: userDetails.FavoriteApps,
      userDetails,
      currentUserRole: null
    }
  })),

  on(userLogoutAction, state => ({
    ...state,
    ...{
      defaultTags: [],
      appTags: [],
      userMenuTags: []
    }
  })),

  on(userProfileAppsResultAction, (state, { userMenuTags }) => ({
    ...state,
    ...{
      userMenuTags
    }
  })),

  on(userProfileSwitchRoleAction, state => ({
    ...state,
    isSwitchingRole: true
  })),

  on(userProfileSwitchRoleResultAction, (state, { roleAssignID, processDesignElements }) => {
    if (
      (state.currentUserRole && state.currentUserRole.RoleAssignID == roleAssignID) ||
      !state.userDetails ||
      !state.userDetails.RoleAssignments ||
      state.userDetails.RoleAssignments.length <= 0
    ) {
      return state;
    }

    let currentUserRole: RoleAssignment;
    if (roleAssignID && roleAssignID > 0) {
      currentUserRole = state.userDetails.RoleAssignments.find(a => a.RoleAssignID == roleAssignID);
    } else if (state.userDetails.RoleAssignments && state.userDetails.RoleAssignments.length > 0) {
      currentUserRole = SharedCommonService.findDefaultRole(state.userDetails);
    } else {
      console.error('Default Role is not configured');
    }

    if (currentUserRole && currentUserRole.Role && currentUserRole.Role.Tags) {
      const defaultTags = currentUserRole.Role.Tags;
      const filteredAppTags = filterAppTags(defaultTags);
      let userMenuTags: dcTag[];
      if (currentUserRole.Role.RoleName == AppConstants.RoleNames.NibrasGuest) {
        userMenuTags = filteredAppTags;
      } else {
        userMenuTags = filterUserProfileApps(state.userDetails.FavoriteApps, filteredAppTags);
      }

      if (
        !currentUserRole.Role.rolePermissionTags ||
        !currentUserRole.Role.rolePermissionTags.length
      )
        currentUserRole.Role.rolePermissionTags = getPermissionTag(currentUserRole);

      let selectedProfile;

      const selectedProfileTagId = +localStorage.getItem(SELECTED_PROFILE_TAG_ID_KEY);
      if (selectedProfileTagId) {
        selectedProfile = currentUserRole.Role.rolePermissionTags.find(
          a => a.TagID === selectedProfileTagId
        );
      }

      // No need random selection for profile tag
      // if (!selectedProfile) {
      //   selectedProfile = currentUserRole.Role.rolePermissionTags
      //     ? currentUserRole.Role.rolePermissionTags[0]
      //     : null;
      // }

      //if (selectedProfile){

      if (selectedProfile && currentUserRole && currentUserRole.Role) {
        selectedProfile = setProfileTagSelection(selectedProfile, currentUserRole);
      }

      localStorage.setItem(
        AppConstants.SELECTED_ROLE_ID_KEY,
        currentUserRole.Role.RoleID.toString()
      );

      if (currentUserRole && currentUserRole.Role)
        currentUserRole.Role.processDesignElements = processDesignElements;

      let userAppsTags: dcTag[];
      if (state.userDetails.UserApps && state.userDetails.UserApps.length > 0) {
        userAppsTags = state.userDetails.UserApps;
      } else {
        console.error('User Apps is not configured');
      }

      const newState = {
        ...state,
        ...{
          currentUserRole,
          defaultTags,
          appTags: filteredAppTags,
          userMenuTags,
          currentProfileTag: selectedProfile,
          isSwitchingRole: false,
          userApps: userAppsTags
        }
      };

      return newState;
    } else {
      console.error('CurrentUserRole.Role.Tags missing');
    }

    return state;
  }),

  on(selectedKPIAction, (state, { selectedKPI }) => ({
    ...state,
    ...{
      selectedKPI
    }
  })),

  on(userProfileSwitchProfileTagAction, (state, { tag }) => {
    const currentProfileTag = setProfileTagSelection(tag, state.currentUserRole);

    return {
      ...state,
      ...{
        currentProfileTag
      }
    };
  }),

  on(setSignalRConnectionAction, (state, { signalRConnection }) => {
    if (!signalRConnection) {
      return state;
    }

    return {
      ...state,
      ...{
        signalRConnection
      }
    };
  }),

  on(dropdownDataAction, (state, { dropdownData }) => ({
    ...state,
    ...{
      dropdownData
    }
  })),

  on(portVisualizationAction, (state, { portTemplates, elemenTypeId }) => ({
    ...state,
    ...{
      portTreeData: {
        portTemplates,
        elemenTypeId
      }
    }
  }))
);

const setProfileTagSelection = (tag: dcTag, role: RoleAssignment) => {
  if (tag && role && role.Role) {
    tag = Object.assign({}, tag);
    if (role.Role.SupportedHierarchy && role.Role.SupportedHierarchy.length) {
      const defaultHierarchy = role.Role.SupportedHierarchy.find(x => x.IsDefault);
      role.Role.HierarchyType = (defaultHierarchy && defaultHierarchy.HierarchyCategoryID) || 0;
    }
    tag.CategoryID = role.Role.HierarchyType || tag.CategoryID;
    localStorage.setItem(SELECTED_PROFILE_TAG_ID_KEY, tag.TagID.toString());
  } else {
    localStorage.setItem(SELECTED_PROFILE_TAG_ID_KEY, '');
  }
  return tag;
};

/**
 * Applies the new set of filters to the products list
 * and returns a new state with the new filtered products.
 */

const filterAppTags = (defaultTags: dcTag[]): dcTag[] =>
  defaultTags.filter(tag => AppConstants.APP_TAG_TYPE_IDs.indexOf(tag.ElementTypeID) > -1);

const filterUserProfileApps = (favoriteAppsTags: dcTag[], filteredAppTags: dcTag[]): dcTag[] => {
  if (!favoriteAppsTags) return [];
  return Object.assign([], favoriteAppsTags).filter(
    tag => filteredAppTags.map(filteredAppTag => filteredAppTag.TagID).indexOf(tag.TagID) > -1
  );
};

const getPermissionTag = (role: RoleAssignment) => {
  const rolePermissionTags: dcTag[] = [];
  permissionTagByKey(role, AppConstants.RolePermissionCategory.READ_ONLY).forEach(
    rolePermissionTag => rolePermissionTags.push(rolePermissionTag)
  );
  permissionTagByKey(role, AppConstants.RolePermissionCategory.READ_WRITE).forEach(
    rolePermissionTag => rolePermissionTags.push(rolePermissionTag)
  );

  return rolePermissionTags;
};

const permissionTagByKey = (role: RoleAssignment, rolePermissionKey: string) => {
  const rolePermissionTags =
    (role && role.Scope && ((role.Scope as any)[rolePermissionKey] as dcTag[])) || null;
  if (rolePermissionTags) {
    return rolePermissionTags.filter(
      rolePermissionTag =>
        rolePermissionTag.CategoryID == TagCategory.ReadOnlyPermission ||
        rolePermissionTag.CategoryID == TagCategory.ReadWritePermission
    );
    //return rolePermissionTags;
    // .forEach(rolePermissionTags => this.rolePermissionTags.push(rolePermissionTags));
  }
  return [];
};

export const coreStoreFeature = createFeature({
  name: 'core',
  reducer: coreStoreReducer
});
